import React, { useState, useEffect } from 'react';
import { X, Check, ArrowRight, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import rosterWizardService from '../../services/rosterWizardService';
import QuickConfigStep, { QuickConfig } from './QuickConfigStep';
import StaffSelectionStep, { StaffSelectionResult } from './StaffSelectionStep';
import RosterPreviewPanel from './RosterPreviewPanel';
import { RosterRequirementSpec } from '../../types/roster';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

function toYMD(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function parseYMD(str: string): Date | null {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
}

interface InlineCalendarProps {
    label: string;
    value: string;
    onChange: (v: string) => void;
    min?: string;
    viewDate: Date;
    onViewChange: (d: Date) => void;
    rangeStart?: string;
    rangeEnd?: string;
}

const InlineCalendar = ({ label, value, onChange, min, viewDate, onViewChange, rangeStart, rangeEnd }: InlineCalendarProps) => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startPad = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: (number | null)[] = [...Array(startPad).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
    while (cells.length % 7 !== 0) cells.push(null);

    const today = toYMD(new Date());

    return (
        <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-700 mb-2">{label}</div>
            <div className="border border-slate-200 rounded-xl bg-white p-4">
                {/* Month nav */}
                <div className="flex items-center justify-between mb-3">
                    <button type="button" onClick={() => onViewChange(new Date(year, month - 1, 1))}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-bold text-slate-800">{MONTHS[month]} {year}</span>
                    <button type="button" onClick={() => onViewChange(new Date(year, month + 1, 1))}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                {/* Day headers */}
                <div className="grid grid-cols-7 mb-1">
                    {DAYS.map((d, i) => (
                        <div key={d} className={`text-center text-xs font-semibold py-1 ${i >= 5 ? 'text-slate-400' : 'text-slate-500'}`}>{d}</div>
                    ))}
                </div>
                {/* Day cells */}
                <div className="grid grid-cols-7 gap-y-1">
                    {cells.map((day, idx) => {
                        if (day === null) return <div key={idx} />;
                        const ymd = toYMD(new Date(year, month, day));
                        const isSelected = ymd === value;
                        const isDisabled = !!(min && ymd < min);
                        const isToday = ymd === today;
                        const inRange = !!(rangeStart && rangeEnd && ymd > rangeStart && ymd < rangeEnd);
                        const col = idx % 7;
                        const isWeekend = col === 5 || col === 6;
                        return (
                            <button key={idx} type="button" disabled={isDisabled} onClick={() => { if (!isDisabled) onChange(ymd); }}
                                className={`h-10 w-full flex items-center justify-center rounded-lg text-sm font-medium transition-all
                                    ${isSelected ? 'bg-blue-600 text-white shadow-sm'
                                        : inRange ? 'bg-blue-50 text-blue-700'
                                        : isToday ? 'bg-blue-50 text-blue-600 font-bold'
                                        : isDisabled ? 'text-slate-200 cursor-not-allowed'
                                        : isWeekend ? 'text-slate-400 hover:bg-slate-100'
                                        : 'text-slate-700 hover:bg-slate-100'}`}>
                                {day}
                            </button>
                        );
                    })}
                </div>
                {/* Today shortcut */}
                <div className="mt-3 pt-3 border-t border-slate-100">
                    <button type="button" onClick={() => onChange(today)}
                        className="w-full text-center text-xs font-semibold text-blue-600 hover:text-blue-700 py-1">
                        Today
                    </button>
                </div>
            </div>
            {value && (
                <p className="mt-2 text-sm text-slate-600 font-medium">
                    {parseYMD(value)?.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
            )}
        </div>
    );
};

interface RosterWizardProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface User {
  id: string;
  name: string;
  role: { name: string };
  userSkills?: { skill: { name: string } }[];
  patternStartDate?: string;
  assignedPatternId?: string | null;
  preferredPatternId?: string | null;
}

const TOTAL_STEPS = 4;

const RosterWizard: React.FC<RosterWizardProps> = ({ onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1: Date State
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startViewDate, setStartViewDate] = useState(() => new Date());
  const [endViewDate, setEndViewDate] = useState(() => {
      const d = new Date(); d.setMonth(d.getMonth() + 1); return d;
  });

  // Step 2: Quick Config State
  const [quickConfig, setQuickConfig] = useState<QuickConfig>({
    heathrowClearerCount: 3,
    minimumDrivers: 2,
    lgwClearerCount: 1,
    controllerCount: 2,
    teamAControllerId: '',
    controllerAnchorDate: '',
    allowAgencyStaff: true,
    busyDates: []
  });

  // Step 3: Staff Selection State
  const [excludedUserIds, setExcludedUserIds] = useState<string[]>([]);
  const [weekendOfficeUserIds, setWeekendOfficeUserIds] = useState<string[]>([]);

  // Step 4: Preview State
  const [previewData, setPreviewData] = useState<{ shifts: any[], users: any[] } | null>(null);
  const [rosterVersionId, setRosterVersionId] = useState<string>('');
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [officeStaffIds, setOfficeStaffIds] = useState<string[]>([]);
  const [controllerUserIds, setControllerUserIds] = useState<string[]>([]);
  const [skillCoverage, setSkillCoverage] = useState<{
    totalClearers: number;
    lhrEligible: number;
    lgwEligible: number;
    driverEligible: number;
    warnings: string[];
  } | null>(null);
  const [allocationWarnings, setAllocationWarnings] = useState<string[]>([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const initialData = await rosterWizardService.getInitialData();
      const { controllers, officeStaff, clearers, warehouseStaff, skillCoverage: coverage } = initialData.data;

      // Normalize users
      const normalizeUser = (u: any, defaultRole: string) => ({
        ...u,
        role: u.role || { name: defaultRole },
      });

      setAllUsers([
        ...controllers.map((c: any) => normalizeUser(c, 'CONTROLLER')),
        ...officeStaff.map((u: any) => normalizeUser(u, 'OFFICE')),
        ...clearers.map((c: any) => normalizeUser(c, 'CLEARER')),
        ...(warehouseStaff || []).map((w: any) => normalizeUser(w, 'WAREHOUSE')),
      ]);
      setOfficeStaffIds(officeStaff.map((u: any) => u.id));
      setControllerUserIds(controllers.map((c: any) => c.id));
      const preferredTeamA = controllers.find((controller: any) =>
        controller.name?.trim().toLowerCase() === 'miguel'
      ) || controllers.find((controller: any) =>
        controller.name?.trim().toLowerCase().includes('miguel')
      ) || controllers[0];
      if (preferredTeamA) {
        setQuickConfig((prev) => ({
          ...prev,
          teamAControllerId: prev.teamAControllerId || preferredTeamA.id,
        }));
      }
      if (coverage) setSkillCoverage(coverage);
    } catch (err: any) {
      console.error("Initial Data Error:", err);
      setError('Failed to load staff data');
    }
  };

  const handleDateNext = async () => {
    setError('');
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    try {
      const val = await rosterWizardService.validateDateRange(new Date(startDate), new Date(endDate));
      if (!val.success || !val.data.isValid) {
        setError(val.data?.errors?.join(', ') || 'Invalid date range');
        setLoading(false);
        return;
      }
      setCurrentStep(2);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStaffSelection = (selection: StaffSelectionResult) => {
    setExcludedUserIds(selection.excludedUserIds);
    setWeekendOfficeUserIds(selection.weekendOfficeUserIds);
    handleGenerate(selection.excludedUserIds, selection.weekendOfficeUserIds);
  };

  const handleGenerate = async (excluded: string[] = excludedUserIds, weekendOffice: string[] = weekendOfficeUserIds) => {
    setError('');
    setLoading(true);

    try {
      // Build requirement spec from quick config
      const days: { date: string; count: number; minimumDrivers: number; driverCount?: number }[] = [];
      const lgwDays: { date: string; count: number; minimumDrivers: number; driverCount?: number }[] = [];

      const start = new Date(startDate);
      const end = new Date(endDate);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        const isBusy = quickConfig.busyDates.includes(dateStr);

        days.push({
          date: dateStr,
          count: isBusy ? quickConfig.heathrowClearerCount * 2 : quickConfig.heathrowClearerCount,
          minimumDrivers: quickConfig.minimumDrivers
        });

        lgwDays.push({
          date: dateStr,
          count: isBusy ? quickConfig.lgwClearerCount * 2 : quickConfig.lgwClearerCount,
          minimumDrivers: 1
        });
      }

      // Filter out excluded users from preferred IDs
      const filteredOfficeIds = officeStaffIds.filter(id => !excluded.includes(id));
      const filteredControllerIds = controllerUserIds.filter(id => !excluded.includes(id));
      const teamAControllerId = quickConfig.teamAControllerId && filteredControllerIds.includes(quickConfig.teamAControllerId)
        ? quickConfig.teamAControllerId
        : filteredControllerIds[0];
      const orderedControllerIds = teamAControllerId
        ? [teamAControllerId, ...filteredControllerIds.filter(id => id !== teamAControllerId)]
        : filteredControllerIds;

      const spec: RosterRequirementSpec = {
        id: '',
        controllers: {
          count: quickConfig.controllerCount,
          pattern: '4-on-4-off',
          preferredUserIds: orderedControllerIds.slice(0, quickConfig.controllerCount)
        },
        heathrow: {
          dailyRequirements: days,
          minimumDrivers: quickConfig.minimumDrivers,
          requiredSkills: ['LHR full Pass']
        },
        gatwick: {
          dailyRequirements: lgwDays,
          requiredSkills: ['LGW full pass', 'can drive'],
          prioritizeDualSkilled: true
        },
        office: {
          weekdayStaff: filteredOfficeIds.length,
          weekendBackup: weekendOffice.length,
          preferredUserIds: [...filteredOfficeIds, ...weekendOffice]
        },
        training: {
          controllerTraining: 0,
          officeTraining: 0,
          scheduleType: 'every_weekend'
        },
        patternOptions: {
          continueExistingPatterns: true,
          startFreshPatterns: false,
          allowAdminOverride: true,
          userPatternChoices: []
        },
        overlapResolution: {
          strategy: 'admin_choice',
          moveToHeathrow: false
        },
        settings: {
          allowAgencyStaff: quickConfig.allowAgencyStaff,
          prioritizeFullTime: true,
          considerLeaveRequests: true,
          considerSwapRequests: true,
          agencyTrainingLevel: 'full_pass',
          excludedUserIds: excluded,
          weekendOfficeUserIds: weekendOffice,
        },
        smartSetup: {
          highVolumeEvents: {
            enabled: quickConfig.busyDates.length > 0,
            dates: quickConfig.busyDates,
            multiplier: 2.0
          },
          controllerAnchorDate: quickConfig.controllerAnchorDate,
          multiRoleStrategy: 'strict'
        }
      };

      // Call auto-allocate
      const res = await rosterWizardService.autoAllocateRoster(spec, rosterVersionId, startDate, endDate);

      if (res.status === 200 || res.data?.success) {
        const data = res.data || res;
        setPreviewData({ shifts: data.assignments, users: allUsers });
        setRosterVersionId(data.versionId || '');
        if (data.warnings && data.warnings.length > 0) {
          setAllocationWarnings(data.warnings);
        }
        setCurrentStep(4);
      } else {
        setError('Allocation failed');
      }
    } catch (e: any) {
      const errMsg = e.response?.data?.error?.message 
        || e.response?.data?.error 
        || e.response?.data?.message 
        || e.message 
        || 'Unknown error';
      setError('Auto-allocation failed: ' + (typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg));
    } finally {
      setLoading(false);
    }
  };

  const stepLabels = ['Date Range', 'Quick Config', 'Staff', 'Preview'];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Roster Wizard</h2>
            <p className="text-sm text-slate-400 mt-0.5">Step {currentStep} of {TOTAL_STEPS} — {stepLabels[currentStep - 1]}</p>
          </div>

          {/* Step Progress */}
          <div className="flex items-center gap-0">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => {
              const done = currentStep > i + 1;
              const active = currentStep === i + 1;
              return (
                <div key={i} className="flex items-center">
                  <div className="flex flex-col items-center gap-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all
                      ${done ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-200'
                        : active ? 'bg-blue-600 text-white shadow-md shadow-blue-200 ring-4 ring-blue-100'
                        : 'bg-slate-100 text-slate-400'}`}>
                      {done ? <Check className="w-4 h-4" /> : i + 1}
                    </div>
                    <span className={`text-[10px] font-medium whitespace-nowrap hidden sm:block
                      ${active ? 'text-blue-600' : done ? 'text-emerald-500' : 'text-slate-300'}`}>
                      {stepLabels[i]}
                    </span>
                  </div>
                  {i < TOTAL_STEPS - 1 && (
                    <div className={`w-10 h-0.5 mb-4 mx-1 rounded-full transition-all ${done ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                  )}
                </div>
              );
            })}
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
            aria-label="Close wizard"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-8 py-5 flex-1 overflow-y-auto">
          {error && <Alert variant="error" className="mb-6">{error}</Alert>}

          {/* STEP 1: DATE RANGE */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                  <CalendarDays className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-800 text-lg">Select Date Range</h3>
                <p className="text-sm text-slate-400 ml-1">— choose the start and end of the new roster</p>
              </div>

              <div className="flex gap-5">
                <InlineCalendar
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  viewDate={startViewDate}
                  onViewChange={setStartViewDate}
                  rangeStart={startDate}
                  rangeEnd={endDate}
                />
                <InlineCalendar
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  min={startDate || undefined}
                  viewDate={endViewDate}
                  onViewChange={setEndViewDate}
                  rangeStart={startDate}
                  rangeEnd={endDate}
                />
              </div>

              {startDate && endDate && new Date(endDate) > new Date(startDate) && (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 rounded-xl text-sm text-blue-700">
                  <CalendarDays className="w-4 h-4 flex-shrink-0" />
                  <span>
                    <strong>{Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24) + 1)} days</strong> selected
                    {' '}({new Date(startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – {new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })})
                  </span>
                </div>
              )}

              <div className="flex justify-end pt-1">
                <Button
                  onClick={handleDateNext}
                  loading={loading}
                  icon={!loading ? <ArrowRight className="w-4 h-4" /> : undefined}
                  size="lg"
                  className="flex-row-reverse"
                >
                  {loading ? 'Validating...' : 'Next'}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 2: QUICK CONFIG */}
          {currentStep === 2 && (
            <QuickConfigStep
              startDate={startDate}
              endDate={endDate}
              config={quickConfig}
              onChange={setQuickConfig}
              onBack={() => setCurrentStep(1)}
              onGenerate={() => setCurrentStep(3)}
              loading={loading}
              staffInfo={skillCoverage ?? undefined}
              controllers={allUsers.filter((user) => controllerUserIds.includes(user.id))}
            />
          )}

          {/* STEP 3: STAFF SELECTION */}
          {currentStep === 3 && (
            <StaffSelectionStep
              users={allUsers}
              startDate={startDate}
              endDate={endDate}
              onBack={() => setCurrentStep(2)}
              onNext={handleStaffSelection}
              loading={loading}
            />
          )}

          {/* STEP 4: PREVIEW */}
          {currentStep === 4 && previewData && (
            <RosterPreviewPanel
              shifts={previewData.shifts}
              users={previewData.users}
              startDate={startDate}
              endDate={endDate}
              onBack={() => setCurrentStep(3)}
              violations={[]}
              warnings={allocationWarnings}
              onEditShift={(shift) => console.log('Edit shift', shift)}
              onApprove={async () => {
                try {
                  console.log('Finishing wizard... Roster saved as DRAFT.');
                  onSuccess();
                  onClose();
                } catch (e: any) {
                  setError('Failed to finalize: ' + e.message);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RosterWizard;
